import React from "react";

const H3 = "text-lg font-semibold text-gray-800 mt-8 mb-3";

function Ref({ n }: { n: number }) {
  return (
    <a href={"#ref-" + n} className="text-brand-500 hover:underline">
      <small>[{n}]</small>
    </a>
  );
}

function About() {
  return (
    <div className="rounded-xl bg-white p-6 md:p-10 shadow-sm border border-gray-100 min-h-[calc(100vh-8rem)]">
      <div className="max-w-3xl text-sm text-gray-600 leading-relaxed">
        <h2 className="text-2xl font-light text-gray-800 mb-6">About ALTRUIST</h2>
        <p>
          <b>ALTRUIST</b> (tot<b>AL</b> variome<b>T</b>ry fo<b>R</b> ts<b>U</b>nam<b>I</b> hazard
          e<b>S</b>tima<b>T</b>ion) is one of eight projects selected worldwide under the Joint Call on
          Coastal Livelihood launched by the AXA Research Fund and the Intergovernmental Oceanographic
          Commission of UNESCO (IOC-UNESCO), within the framework of the United Nations Decade of Ocean
          Science for Sustainable Development <Ref n={1} />.
        </p>

        <h3 className={H3}>Our goal</h3>
        <div className="space-y-4">
          <p>
            ALTRUIST aims to improve the reliability and accuracy of real-time earthquake and tsunami
            monitoring and warning. It does this by using Global Navigation Satellite System (GNSS)
            observations to monitor two layers at the same time: the ground and the ionosphere.
          </p>
          <p>
            When a large earthquake strikes, GNSS stations record the ground shaking and the co-seismic
            displacement. These provide the magnitude and direction of the ground motion, key information
            for estimating the deformation of the seafloor. Minutes later, the atmospheric waves generated
            by the earthquake and the tsunami reach the ionosphere and perturb its Total Electron Content
            (TEC). These perturbations can confirm the tsunamigenic potential of the event. Combining both
            layers gives warning systems an independent, complementary source of information.
          </p>
        </div>

        <h3 className={H3}>The Total Variometric Approach</h3>
        <div className="space-y-4">
          <p>
            ALTRUIST is built on the Total Variometric Approach (TVA) <Ref n={2} />. GNSS variometry uses
            single time differences of suitable linear combinations of GNSS carrier-phase observations.
            This allows a single receiver to deliver valuable information in real time and in a
            stand-alone mode, without reference stations or external corrections.
          </p>
          <p>
            TVA jointly runs two algorithms, both developed at Sapienza University of Rome, on the same
            real-time GNSS data streams:
          </p>
          <ul className="list-disc ml-5 space-y-2">
            <li>
              <b>VADASE</b> (Variometric Approach for Displacement Analysis Stand-alone Engine){" "}
              <Ref n={3} /> estimates ground velocities and co-seismic displacements;
            </li>
            <li>
              <b>VARION</b> (Variometric Approach for Real-Time IONosphere Observation) <Ref n={4} />{" "}
              estimates TEC variations to detect ionospheric disturbances.
            </li>
          </ul>
        </div>

        <h3 className={H3}>Where it runs</h3>
        <div className="space-y-4">
          <p>
            ALTRUIST currently processes real-time GNSS streams from selected stations worldwide. This
            dashboard shows the resulting ground and ionospheric time series in real time, to test how
            useful multi-layer GNSS observations are for tsunami hazard assessment.
          </p>
          <p>
            Because the method needs nothing more than a real-time GNSS stream, it can be applied to any
            GNSS network in the world.
          </p>
        </div>

        <h3 className={H3}>References</h3>
        <ol className="list-decimal ml-5 space-y-1">
          <li id="ref-1">
            <a href="https://axa-research.org/funded-projects/climate-environment/mitigating-tsunamis-threats-and-destructive-impacts-through-enhanced-navigation-satellite-system" target="_blank" rel="noreferrer" className="text-brand-500 hover:underline">
              ALTRUIST – AXA Research Fund
            </a>
          </li>
          <li id="ref-2">
            <a href="https://www.nature.com/articles/s41598-021-82532-6" target="_blank" rel="noreferrer" className="text-brand-500 hover:underline">
              Ravanelli, M., et al. (2021). GNSS total variometric approach: first demonstration of a tool
              for real-time tsunami genesis estimation. <i>Scientific Reports</i>, 11(1).
            </a>
          </li>
          <li id="ref-3">
            <a href="https://agupubs.onlinelibrary.wiley.com/doi/full/10.1029/2010JB007941" target="_blank" rel="noreferrer" className="text-brand-500 hover:underline">
              Colosimo, G., et al. (2011). <i>Journal of Geophysical Research: Solid Earth</i>, 116(B11).
            </a>
          </li>
          <li id="ref-4">
            <a href="https://www.nature.com/articles/srep46607" target="_blank" rel="noreferrer" className="text-brand-500 hover:underline">
              Savastano, G., et al. (2017). <i>Scientific Reports</i>, 7(1), 46607.
            </a>
          </li>
        </ol>
      </div>
    </div>
  );
}

export default About;
